import gsap from "gsap";
import { encodeParticipantsToURL } from "@/config";
import type { Participant } from "@/types";
import { colorToHex } from "@/utils/colorToHex";

export class UIManager {
  private copyLinkResetTimeout: number | null = null;
  private editBtn: HTMLButtonElement;
  private startBtn: HTMLButtonElement;

  private resetBtn: HTMLButtonElement;
  private editorOverlay: HTMLElement;
  private editorForm: HTMLFormElement;
  private editorRows: HTMLElement;
  private saveEditorBtn: HTMLButtonElement;
  private cancelEditorBtn: HTMLButtonElement;
  private copyLinkBtn: HTMLButtonElement;
  private resultOverlay: HTMLElement;
  private resultContent: HTMLElement;
  private winnerThemeEl: HTMLElement;
  private winnerParticipantEl: HTMLElement;
  private participantThemesEl: HTMLElement;
  private titleEl: HTMLElement;

  constructor() {
    this.editBtn = document.getElementById("edit-btn") as HTMLButtonElement;
    this.startBtn = document.getElementById("start-btn") as HTMLButtonElement;

    this.resetBtn = document.getElementById("reset-btn") as HTMLButtonElement;
    this.editorOverlay = document.getElementById("editor-overlay")!;
    this.editorForm = document.getElementById("editor-form") as HTMLFormElement;
    this.editorRows = this.editorForm.querySelector(".editor-rows")!;
    this.saveEditorBtn = document.getElementById("save-editor-btn") as HTMLButtonElement;
    this.cancelEditorBtn = document.getElementById("cancel-editor-btn") as HTMLButtonElement;
    this.copyLinkBtn = document.getElementById("copy-link-btn") as HTMLButtonElement;
    this.resultOverlay = document.getElementById("result-overlay")!;
    this.resultContent = document.getElementById("result-content")!;
    this.winnerThemeEl = document.getElementById("winner-theme")!;
    this.winnerParticipantEl = document.getElementById("winner-participant")!;
    this.participantThemesEl = document.getElementById("participant-themes")!;
    this.titleEl = document.getElementById("title")!;
  }

  bindEvents(handlers: {
    start: () => void;
    reset: () => void;
    edit: () => void;
    saveParticipants: (participants: Participant[]) => void;
  }): void {
    this.startBtn.addEventListener("click", handlers.start);
    this.resetBtn.addEventListener("click", handlers.reset);
    this.editBtn.addEventListener("click", handlers.edit);
    this.cancelEditorBtn.addEventListener("click", () => this.closeEditor());
    this.saveEditorBtn.addEventListener("click", () => {
      handlers.saveParticipants(this.readEditorParticipants());
    });
    this.copyLinkBtn.addEventListener("click", () => this.copyShareLink());
  }

  openEditor(participants: Participant[]): void {
    this.editorRows.innerHTML = "";
    participants.forEach((participant, index) => {
      const row = document.createElement("div");
      row.className = "editor-row";
      const colorHex = colorToHex(participant.color);
      row.style.setProperty("--row-accent", colorHex);
      row.innerHTML = `
        <label>Peserta ${index + 1}</label>
        <div class="row-fields">
          <input type="text" name="name-${index}" value="${this.escapeHtml(participant.name)}" placeholder="Nama">
          <input type="text" name="theme-${index}" value="${this.escapeHtml(participant.theme)}" placeholder="Tema">
          <input type="color" name="color-${index}" value="${colorHex}">
        </div>
        <p class="row-note">warna peserta • ${colorHex.toUpperCase()}</p>
      `;
      const colorInput = row.querySelector(`[name="color-${index}"]`) as HTMLInputElement;
      colorInput.addEventListener("input", () => {
        row.style.setProperty("--row-accent", colorInput.value);
        const rowNote = row.querySelector(".row-note");
        if (rowNote) {
          rowNote.textContent = `warna peserta • ${colorInput.value.toUpperCase()}`;
        }
      });
      this.editorRows.appendChild(row);
    });
    this.editorOverlay.classList.remove("hidden");
  }

  closeEditor(): void {
    this.editorOverlay.classList.add("hidden");
  }

  readEditorParticipants(): Participant[] {
    const participants: Participant[] = [];
    for (let i = 0; i < 4; i++) {
      const name =
        (this.editorForm.querySelector(`[name="name-${i}"]`) as HTMLInputElement).value.trim() ||
        `Peserta ${i + 1}`;
      const theme =
        (this.editorForm.querySelector(`[name="theme-${i}"]`) as HTMLInputElement).value.trim() ||
        `Tema ${i + 1}`;
      const colorVal = (this.editorForm.querySelector(`[name="color-${i}"]`) as HTMLInputElement)
        .value;
      const color = parseInt(colorVal.slice(1), 16);
      participants.push({ name, theme, color });
    }
    return participants;
  }

  setEditEnabled(enabled: boolean): void {
    this.editBtn.classList.toggle("hidden", !enabled);
  }

  async copyShareLink(): Promise<void> {
    const participants = this.readEditorParticipants();
    const shareURL = new URL(window.location.href);
    shareURL.search = encodeParticipantsToURL(participants).slice(1);

    try {
      await this.writeClipboardText(shareURL.toString());
      this.flashCopyButtonLabel("TERSALIN!");
    } catch {
      this.flashCopyButtonLabel("GAGAL SALIN");
    }
  }

  hideTitle(): void {
    gsap.to(this.titleEl, {
      opacity: 0,
      y: -40,
      duration: 0.5,
      onComplete: () => {
        this.titleEl.classList.add("hidden");
        this.titleEl.style.opacity = "";
        this.titleEl.style.transform = "";
      },
    });
    this.startBtn.classList.add("hidden");
  }

  showParticipantThemes(participants: Participant[]): void {
    this.participantThemesEl.innerHTML = participants
      .map((participant, index) => {
        const background = colorToHex(participant.color);
        const foreground = this.getReadableTextColor(participant.color);
        return `
          <div
            class="theme-item"
            style="--theme-bg: ${background}; --theme-fg: ${foreground};"
          >
            <span class="theme-badge">P${index + 1}</span>
            <div class="theme-copy">
              <span class="theme-name">${this.escapeHtml(participant.name)}</span>
              <span class="theme-theme">"${this.escapeHtml(participant.theme)}"</span>
            </div>
          </div>
        `;
      })
      .join("");
    this.participantThemesEl.style.opacity = "1";
    this.participantThemesEl.classList.remove("hidden");
    gsap.from(this.participantThemesEl, { opacity: 0, x: 40, duration: 0.45, ease: "power2.out" });
  }

  hideParticipantThemes(): void {
    this.participantThemesEl.classList.add("hidden");
  }

  fadeOutParticipantThemes(): void {
    gsap.to(this.participantThemesEl, {
      opacity: 0,
      duration: 0.3,
      onComplete: () => this.participantThemesEl.classList.add("hidden"),
    });
  }

  showResetButton(): void {
    this.resetBtn.classList.remove("hidden");
  }

  showResult(winner: Participant): void {
    const colorHex = colorToHex(winner.color);
    gsap.killTweensOf(this.resultContent);
    this.winnerThemeEl.textContent = `"${winner.theme}"`;
    this.winnerThemeEl.style.color = colorHex;
    this.winnerParticipantEl.textContent = `Diusulkan oleh: ${winner.name}`;
    this.resultContent.style.borderColor = colorHex;
    this.resultContent.style.setProperty("--winner-color", colorHex);
    this.resultOverlay.style.setProperty("--winner-color", colorHex);
    this.resultContent.style.transform = "";
    this.resultOverlay.classList.remove("hidden");
    gsap.from(this.resultContent, {
      scale: 0,
      rotation: -0.08,
      duration: 0.7,
      ease: "elastic.out(1, 0.5)",
    });
  }

  hideResult(immediate = false): void {
    gsap.killTweensOf(this.resultContent);

    if (immediate) {
      this.resultOverlay.classList.add("hidden");
      this.resultContent.style.transform = "";
      this.resultContent.style.removeProperty("--winner-color");
      this.resultOverlay.style.removeProperty("--winner-color");
      return;
    }

    gsap.to(this.resultContent, {
      scale: 0,
      duration: 0.3,
      ease: "back.in(1.7)",
      onComplete: () => {
        this.resultOverlay.classList.add("hidden");
        this.resultContent.style.transform = "";
        this.resultContent.style.removeProperty("--winner-color");
        this.resultOverlay.style.removeProperty("--winner-color");
      },
    });
  }

  resetAll(): void {
    this.resetBtn.classList.add("hidden");
    this.startBtn.classList.remove("hidden");
    this.titleEl.classList.remove("hidden");
    this.participantThemesEl.classList.add("hidden");
    this.participantThemesEl.style.opacity = "";
    this.resultOverlay.classList.add("hidden");
    this.resultContent.style.transform = "";
    this.winnerThemeEl.textContent = "";
    this.winnerThemeEl.style.color = "";
    this.winnerParticipantEl.textContent = "";
    this.resultContent.style.borderColor = "";
    this.resultContent.style.removeProperty("--winner-color");
    this.resultOverlay.style.removeProperty("--winner-color");
  }

  private getReadableTextColor(color: number): string {
    const red = (color >> 16) & 0xff;
    const green = (color >> 8) & 0xff;
    const blue = color & 0xff;
    const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;
    return luminance > 0.62 ? "#005F60" : "#FFFFFF";
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  private async writeClipboardText(text: string): Promise<void> {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch {
        // Fall through to execCommand fallback for stricter browser contexts.
      }
    }

    this.copyTextWithExecCommand(text);
  }

  private copyTextWithExecCommand(text: string): void {
    const textarea = document.createElement("textarea");
    const activeElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    textarea.value = text;
    textarea.setAttribute("readonly", "true");
    textarea.style.position = "fixed";
    textarea.style.top = "0";
    textarea.style.left = "-9999px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);

    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, textarea.value.length);

    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    activeElement?.focus();

    if (!copied) {
      throw new Error("Clipboard copy failed");
    }
  }

  private flashCopyButtonLabel(label: string): void {
    if (this.copyLinkResetTimeout !== null) {
      window.clearTimeout(this.copyLinkResetTimeout);
    }

    const originalText = "SALIN LINK";
    this.copyLinkBtn.textContent = label;
    this.copyLinkResetTimeout = window.setTimeout(() => {
      this.copyLinkBtn.textContent = originalText;
      this.copyLinkResetTimeout = null;
    }, 2000);
  }
}
